import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { properties } from '../../../environments/properties';
import { B4UPLUS_ROUTES } from '../constants/b4uplus-routes';
import { _windowUtil } from '../utils/ui-utils';
import { SubscriptionService } from './subscription.service';
import { ProgramClassification, ProgramClassifier } from './program-classifier.service';

// Which of the guarded route families the current navigation belongs to. The destination policy
// (resolveDestination) is keyed on this so the four routes that used to need three separate guards
// now share one classify pipeline.
type RouteGroup = 'ENTRY' | 'MANAGE' | 'SUBSCRIBE' | 'FRESHPASS';

// Outcome of classifying the current user. `classification` mirrors ProgramClassifier; 'error' is a
// distinct value because the FRESHPASS group treats a lookup failure (=> /foruplus) differently from
// an unclassified/anonymous user (=> stay).
interface ClassifyOutcome {
    classification: ProgramClassification | 'error';
    cancelled: boolean;
}

/**
 * Single guard for the ForU+/legacy redirect rules. It replaces the former trio of guards
 * (B4uplusRedirectGuard + ForUPlusEntryGuard + LegacySubscribeRedirectGuard), which all performed
 * the same three steps — acquire the session token, fetch getSubscription, classify the user — and
 * only differed in where each classification should land per route. That per-route policy now lives
 * in resolveDestination()/flagOffDestination(), and classification is delegated to ProgramClassifier.
 *
 * Guards run before resolvers, so route.data['tokenObject'] may be empty on first navigation; when
 * it is we call getUserInfoCall() directly (the same call the TokenUserInfo resolver makes) to obtain
 * the token before classifying. Anonymous/no-token users classify as 'none'.
 */
@Injectable({ providedIn: 'root' })
export class B4uplusRedirectGuard implements CanActivate {

    public windowUtil = _windowUtil(window);

    constructor(
        private router: Router,
        private subscriptionService: SubscriptionService,
        private programClassifier: ProgramClassifier
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        // Start each navigation clean so a failed/empty guard fetch never leaves a stale stash for
        // the page component to pick up.
        this.subscriptionService.clearStashedSubscriptionDetails();
        const group = this.resolveGroup(state.url);

        if (!properties.featureFlags.enableB4UPlusLaunch) {
            return this.flagOffDestination(group);
        }
        return this.classify(route).pipe(
            map((outcome) => this.resolveDestination(group, outcome))
        );
    }

    private resolveGroup(url: string): RouteGroup {
        if (url.startsWith('/freshpass/manage/subscribe')) {
            return 'SUBSCRIBE';
        }
        if (url.startsWith('/freshpass/manage')) {
            return 'FRESHPASS';
        }
        if (url.startsWith(`${B4UPLUS_ROUTES.LANDING}/manage`)) {
            return 'MANAGE';
        }
        if (url.startsWith(B4UPLUS_ROUTES.LANDING)) {
            return 'ENTRY';
        }
        // Guard is only attached to the families above; default to the most permissive group.
        return 'FRESHPASS';
    }

    // Pre-launch (flag OFF): the new /foruplus pages must never render, so the enrollment entry and
    // the manage pages bounce to the legacy subscribe route; legacy pages are untouched.
    private flagOffDestination(group: RouteGroup): boolean {
        if (group === 'ENTRY' || group === 'MANAGE') {
            this.windowUtil.setWindowLocationHref('/freshpass/manage/subscribe');
            return false;
        }
        return true;
    }

    private resolveDestination(group: RouteGroup, { classification, cancelled }: ClassifyOutcome): boolean | UrlTree {
        // An enrolled ForU+ subscriber only "belongs" on the manage pages while their plan is active;
        // a cancelled ForU+ subscriber is treated like any non-member (this mirrors the previous
        // B4uplusRedirectGuard, which excluded CANCELLED from its B4U+ check).
        const activeB4UPlus = classification === 'foruplus' && !cancelled;
        const activeLegacy = classification === 'legacy' && !cancelled;
        // With enableB4UPlusLaunch ON, any cancelled subscriber (regardless of planGeneration)
        // is directed to /foruplus to re-enroll and never to /freshpass/manage/subscribe.
        const cancelledB4UPlus = classification === 'foruplus' && cancelled;
        const confirmationPath = properties.programType.freshpass.urlPath.CONFIRMATION_PATH;

        switch (group) {
            case 'ENTRY':
                // /foruplus enrollment entry: send already-enrolled users to their manage page,
                // everyone else (including cancelled users of any plan) stays to re-enroll.
                // Destinations that cross into the full-header shell use a hard page load
                // (server.js only re-evaluates the AEM header on a fresh request).
                if (activeLegacy) {
                    this.windowUtil.setWindowLocationHref(confirmationPath);
                    return false;
                }
                if (activeB4UPlus) {
                    this.windowUtil.setWindowLocationHref(B4UPLUS_ROUTES.MANAGE_PLAN);
                    return false;
                }
                // Cancelled ForU+ / none / error: stay on /foruplus to re-enroll.
                return true;

            case 'SUBSCRIBE':
                // Legacy /freshpass/manage/subscribe entry (flag ON): route directly to the user's
                // destination instead of bouncing through /foruplus; unknown/anonymous => enroll.
                // Legacy/active-ForU+ destinations cross into the full-header shell.
                // Any cancelled subscriber (regardless of planGeneration) lands on /foruplus to
                // re-enroll — they must never be left on /freshpass/manage/subscribe.
                if (cancelled) {
                    return this.router.createUrlTree([B4UPLUS_ROUTES.LANDING]);
                }
                if (activeLegacy) {
                    this.windowUtil.setWindowLocationHref(confirmationPath);
                    return false;
                }
                if (activeB4UPlus) {
                    this.windowUtil.setWindowLocationHref(B4UPLUS_ROUTES.MANAGE_PLAN);
                    return false;
                }
                return this.router.createUrlTree([B4UPLUS_ROUTES.LANDING]);

            case 'MANAGE':
                // /foruplus/manage/*: only an active ForU+ subscriber may stay. Everyone else
                // (cancelled any plan, legacy, none) bounces to /foruplus, crossing shells.
                if (activeB4UPlus) {
                    return true;
                }
                this.windowUtil.setWindowLocationHref(B4UPLUS_ROUTES.LANDING);
                return false;

            case 'FRESHPASS':
            default:
                // Legacy /freshpass/manage* pages: pull an active ForU+ subscriber over to the new
                // manage experience; a cancelled ForU+ subscriber is normalized to /foruplus; on a
                // lookup error send to /foruplus (shell crossing); otherwise leave them be (a
                // cancelled legacy subscriber stays — legacy manage renders its own re-subscribe UI).
                if (activeB4UPlus) {
                    return this.router.createUrlTree([B4UPLUS_ROUTES.MANAGE_PLAN]);
                }
                if (cancelledB4UPlus) {
                    this.windowUtil.setWindowLocationHref(B4UPLUS_ROUTES.LANDING);
                    return false;
                }
                if (classification === 'error') {
                    this.windowUtil.setWindowLocationHref(B4UPLUS_ROUTES.LANDING);
                    return false;
                }
                return true;
        }
    }

    private classify(route: ActivatedRouteSnapshot): Observable<ClassifyOutcome> {
        const tokenObject = route?.data?.['tokenObject'];
        if (tokenObject?.SWY_SHOP_TOKEN) {
            return this.classifyWithToken(tokenObject);
        }
        return this.subscriptionService.getUserInfoCall().pipe(
            switchMap((userInfo: any) => {
                if (!userInfo?.SWY_SHOP_TOKEN) {
                    return of<ClassifyOutcome>({ classification: 'none', cancelled: false });
                }
                return this.classifyWithToken({ ...userInfo, authToken: userInfo.SWY_SHOP_TOKEN });
            }),
            catchError(() => of<ClassifyOutcome>({ classification: 'error', cancelled: false }))
        );
    }

    private classifyWithToken(tokenObject: any): Observable<ClassifyOutcome> {
        const userInfo = { ...tokenObject, authToken: tokenObject.authToken || tokenObject.SWY_SHOP_TOKEN };
        const env = this.subscriptionService.getEnvironment();
        return this.subscriptionService.getUserSubscriptionDetails(userInfo, env).pipe(
            tap((response) => this.subscriptionService.stashSubscriptionDetails(userInfo.customerId, response)),
            map((response): ClassifyOutcome => {
                const data = response?.Data;
                return {
                    classification: this.programClassifier.classify(data),
                    cancelled: this.programClassifier.isCancelled(data)
                };
            }),
            catchError(() => of<ClassifyOutcome>({ classification: 'error', cancelled: false }))
        );
    }
}
